import { useEffect, useState, useCallback } from "react";
import {
  X, Activity, Users, ScrollText, Coins, Zap, Flag, RefreshCw, Loader2, Check, Terminal,
} from "lucide-react";
import { api } from "@/lib/api";
import { useDevMode } from "@/context/DevModeContext";
import { toast } from "sonner";

/**
 * DevPanel — floating right-side drawer visible only when:
 *   · user.is_admin === true, AND
 *   · devMode toggle is ON (persisted per-browser).
 *
 * Sections (tabs):
 *   1. Health — /api/dev/status (uptime, DB, active users, gens)
 *   2. Logs — /api/dev/logs (recent generations)
 *   3. Transactions — /api/dev/transactions (credit ledger)
 *   4. Test Gen — POST /api/dev/test-generate (no fal, no credits)
 *   5. Feature Flags — PUT /api/dev/flags (persisted globally)
 *
 * Distinct gold border + gold header stripe so the operator never mistakes
 * it for a normal user surface.
 */
const TABS = [
  { key: "health",  label: "Health",       icon: Activity },
  { key: "logs",    label: "Gen Logs",     icon: ScrollText },
  { key: "tx",      label: "Credit Log",   icon: Coins },
  { key: "test",    label: "Test Gen",     icon: Zap },
  { key: "flags",   label: "Feature Flags", icon: Flag },
];

const FLAG_LABELS = {
  timeline_editor:    "Video Timeline Editor",
  storyboard:         "Storyboard Tool",
  three_d_generator:  "3D Generator (360° / Turntable)",
  qr_generator:       "QR Generator",
  cast_library:       "Cast Library",
  character_sheets:   "Character Sheets (Turnaround)",
  ensemble_prompts:   "Ensemble Prompts",
  gallery:            "Gallery",
  avatar_studio:      "Avatar Studio",
  voice_cloning:      "Voice Cloning",
  story_reels:        "Story Reels",
  watermark_enabled:  "Watermark (GENEO AI overlay)",
};

// Copy for the watermark row — sits under the label so the operator knows
// exactly what the flag does before flipping it.
const FLAG_DESCRIPTIONS = {
  watermark_enabled: "Bakes a gold/cyan GENEO AI pill into the bottom-right corner of every generated image and every generated video. Applied at storage time — no re-upload cost after the fact.",
};

export default function DevPanel() {
  const { isAdmin, devMode, setDevMode, flags, updateFlags, refreshFlags } = useDevMode();
  const [open, setOpen] = useState(true);
  const [tab, setTab] = useState("health");
  const [status, setStatus] = useState(null);
  const [logs, setLogs] = useState([]);
  const [tx, setTx] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testPrompt, setTestPrompt] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testBusy, setTestBusy] = useState(false);
  const [flagBusy, setFlagBusy] = useState(false);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/dev/status"); setStatus(data); }
    catch (e) { toast.error("Dev status failed"); }
    finally { setLoading(false); }
  }, []);
  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/dev/logs?limit=25"); setLogs(data.items || []); }
    catch { toast.error("Dev logs failed"); }
    finally { setLoading(false); }
  }, []);
  const fetchTx = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/dev/transactions?limit=25"); setTx(data.items || []); }
    catch { toast.error("Dev transactions failed"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    if (!(isAdmin && devMode && open)) return;
    if (tab === "health") fetchHealth();
    else if (tab === "logs") fetchLogs();
    else if (tab === "tx") fetchTx();
    else if (tab === "flags") refreshFlags();
  }, [tab, isAdmin, devMode, open, fetchHealth, fetchLogs, fetchTx, refreshFlags]);

  if (!isAdmin || !devMode) return null;

  const runTestGen = async () => {
    if (!testPrompt.trim()) { toast.error("Enter a prompt first."); return; }
    setTestBusy(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/dev/test-generate", { prompt: testPrompt, mode: "image" });
      setTestResult(data);
      toast.success("Test generation OK — 0 credits deducted.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTestBusy(false);
    }
  };

  const toggleFlag = async (key) => {
    setFlagBusy(true);
    try {
      const next = { ...flags, [key]: !flags[key] };
      await updateFlags(next);
      toast.success(`${FLAG_LABELS[key] || key} → ${next[key] ? "ON" : "OFF"} (global)`);
    } catch {
      toast.error("Failed to update flag");
    } finally {
      setFlagBusy(false);
    }
  };

  return (
    <>
      {/* Collapsed pill (bottom-right) — one tap to expand the panel */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="dev-panel-open"
          title="Open Developer Panel"
          className="fixed bottom-24 right-4 z-[70] inline-flex items-center gap-2 px-4 py-2.5 rounded-full border-2 border-[#F2C94C] bg-[#0A0A0A] text-[#F2C94C] font-mono-hy text-[10px] tracking-[0.24em] shadow-[0_0_28px_-6px_rgba(242,201,76,0.75)] hover:bg-[#F2C94C]/10"
        >
          <Terminal className="w-3.5 h-3.5" /> DEV MODE
        </button>
      )}

      {/* Full drawer */}
      {open && (
        <aside
          data-testid="dev-panel"
          className="fixed top-20 right-4 z-[70] w-[380px] max-h-[calc(100vh-6rem)] flex flex-col rounded-2xl border-2 border-[#F2C94C] bg-gradient-to-br from-[#120E15] via-[#0e0b13] to-[#0A0A0A] shadow-[0_0_50px_-10px_rgba(242,201,76,0.6),0_20px_60px_-15px_rgba(0,0,0,0.9)] pointer-events-auto"
        >
          {/* Gold stripe header — makes Dev UI instantly recognizable */}
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F2C94C]/40 bg-[#F2C94C]/[0.06]">
            <div className="flex items-center gap-2">
              <Terminal className="w-3.5 h-3.5 text-[#F2C94C]" />
              <span className="font-mono-hy text-[10px] tracking-[0.28em] text-[#F2C94C]">DEVELOPER MODE</span>
              <span className="font-mono-hy text-[8px] px-1.5 py-0.5 rounded bg-[#F2C94C] text-[#0A0A0A] tracking-widest">ADMIN</span>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="dev-panel-close"
              className="w-7 h-7 rounded-full border border-white/15 hover:border-[#F2C94C] hover:text-[#F2C94C] text-white/70 flex items-center justify-center transition-all"
              aria-label="Close dev panel"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Tab bar */}
          <div className="flex items-center gap-1 px-3 py-2 border-b border-white/[0.06] overflow-x-auto">
            {TABS.map((t) => {
              const Ico = t.icon;
              const active = tab === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setTab(t.key)}
                  data-testid={`dev-tab-${t.key}`}
                  className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full font-mono-hy text-[9px] tracking-[0.16em] border transition-all flex-shrink-0 ${
                    active
                      ? "border-[#F2C94C] bg-[#F2C94C]/15 text-[#F2C94C]"
                      : "border-white/10 text-white/60 hover:border-[#F2C94C]/40 hover:text-[#F2C94C]"
                  }`}
                >
                  <Ico className="w-3 h-3" /> {t.label}
                </button>
              );
            })}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 text-white">
            {tab === "health" && (
              <div className="space-y-2" data-testid="dev-tab-body-health">
                <div className="flex items-center justify-between">
                  <span className="font-mono-hy text-[9px] tracking-widest text-[#F2C94C]">SERVER · LIVE</span>
                  <button
                    type="button"
                    onClick={fetchHealth}
                    disabled={loading}
                    data-testid="dev-refresh-health"
                    className="text-[#00E5FF] hover:text-white disabled:opacity-40"
                  >
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </button>
                </div>
                {status ? (
                  <div className="grid grid-cols-2 gap-2">
                    <Stat label="Uptime"        value={fmtUptime(status.uptime_s)} good />
                    <Stat label="DB"            value={status.db_ok ? "OK" : "DOWN"} good={status.db_ok} bad={!status.db_ok} />
                    <Stat label="Active · 15m"  value={String(status.active_users_15m ?? "?")} accent="cyan" />
                    <Stat label="Total Users"   value={String(status.total_users ?? "?")} />
                    <Stat label="Gens · 24h"    value={String(status.generations_24h ?? "?")} accent="cyan" />
                    <Stat label="Gens · Total"  value={String(status.generations_total ?? "?")} />
                  </div>
                ) : (
                  <div className="text-white/50 font-mono-hy text-[10px]">Loading…</div>
                )}
              </div>
            )}

            {tab === "logs" && (
              <div className="space-y-2" data-testid="dev-tab-body-logs">
                <div className="flex items-center justify-between">
                  <span className="font-mono-hy text-[9px] tracking-widest text-[#F2C94C]">RECENT GENERATIONS · 25</span>
                  <button type="button" onClick={fetchLogs} className="text-[#00E5FF]" data-testid="dev-refresh-logs">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <ul className="space-y-1.5">
                  {logs.map((l) => (
                    <li key={l.generation_id} className="rounded-lg border border-white/[0.06] px-2.5 py-1.5 bg-[#0A0A0A]/40">
                      <div className="flex items-center justify-between font-mono-hy text-[9px]">
                        <span className="text-[#F2C94C]">{l.type}</span>
                        <span className="text-white/40">{fmtTimeAgo(l.created_at)}</span>
                      </div>
                      <div className="font-body text-[11px] text-white/85 line-clamp-2 mt-0.5">{l.prompt || "—"}</div>
                    </li>
                  ))}
                  {logs.length === 0 && !loading && <li className="font-mono-hy text-[10px] text-white/45">No entries.</li>}
                </ul>
              </div>
            )}

            {tab === "tx" && (
              <div className="space-y-2" data-testid="dev-tab-body-tx">
                <div className="flex items-center justify-between">
                  <span className="font-mono-hy text-[9px] tracking-widest text-[#F2C94C]">CREDIT LEDGER · 25</span>
                  <button type="button" onClick={fetchTx} className="text-[#00E5FF]" data-testid="dev-refresh-tx">
                    {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </button>
                </div>
                <ul className="space-y-1.5">
                  {tx.map((t, i) => (
                    <li key={t.transaction_id || t.id || t._id || `${t.created_at || ""}-${i}`} className="rounded-lg border border-white/[0.06] px-2.5 py-1.5 bg-[#0A0A0A]/40">
                      <div className="flex items-center justify-between font-mono-hy text-[9px]">
                        <span className="text-[#F2C94C]">{t.kind || "tx"}</span>
                        <span className={`${(t.delta || 0) >= 0 ? "text-[#27AE60]" : "text-[#EB5757]"}`}>
                          {(t.delta || 0) >= 0 ? "+" : ""}{t.delta}
                        </span>
                      </div>
                      <div className="font-body text-[10px] text-white/70">{t.reason || t.user_id || "—"}</div>
                    </li>
                  ))}
                  {tx.length === 0 && !loading && <li className="font-mono-hy text-[10px] text-white/45">No transactions yet.</li>}
                </ul>
              </div>
            )}

            {tab === "test" && (
              <div className="space-y-2" data-testid="dev-tab-body-test">
                <div className="font-mono-hy text-[9px] tracking-widest text-[#F2C94C]">DRY-RUN GENERATION</div>
                <div className="font-body text-[11px] text-white/70 leading-snug">
                  Fires the prompt path end-to-end without calling fal.ai. <strong className="text-[#F2C94C]">Zero credits</strong> deducted.
                </div>
                <textarea
                  value={testPrompt}
                  onChange={(e) => setTestPrompt(e.target.value)}
                  placeholder="a hero standing under a Nubian sun…"
                  data-testid="dev-test-prompt"
                  className="w-full h-20 rounded-lg bg-[#0A0A0A] border border-white/10 focus:border-[#F2C94C] px-3 py-2 text-[12px] text-white outline-none resize-none"
                />
                <button
                  type="button"
                  onClick={runTestGen}
                  disabled={testBusy}
                  data-testid="dev-test-run"
                  className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-[#F2C94C] text-[#0A0A0A] font-mono-hy text-[10px] tracking-[0.18em] disabled:opacity-40"
                >
                  {testBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
                  RUN TEST GENERATION
                </button>
                {testResult && (
                  <pre data-testid="dev-test-result" className="text-[10px] font-mono-hy text-[#00E5FF] bg-[#0A0A0A] border border-[#00E5FF]/20 rounded-lg p-2 overflow-x-auto">
                    {JSON.stringify(testResult, null, 2)}
                  </pre>
                )}
              </div>
            )}

            {tab === "flags" && (
              <div className="space-y-2" data-testid="dev-tab-body-flags">
                <div className="font-mono-hy text-[9px] tracking-widest text-[#F2C94C]">GLOBAL FEATURE FLAGS</div>
                <div className="font-body text-[10px] text-white/60 leading-snug">
                  Persisted in MongoDB. Toggling here hides / shows the tool for <strong className="text-[#F2C94C]">every user</strong> in real time. Admins always see everything regardless.
                </div>
                <ul className="space-y-1.5 mt-1">
                  {Object.keys(FLAG_LABELS).map((key) => {
                    const on = !!flags[key];
                    const desc = FLAG_DESCRIPTIONS[key];
                    return (
                      <li key={key} className="flex items-start justify-between rounded-lg border border-white/[0.06] px-2.5 py-2 bg-[#0A0A0A]/40">
                        <div className="min-w-0 pr-3">
                          <div className="font-body text-[11px] text-white/90">{FLAG_LABELS[key]}</div>
                          {desc && (
                            <div className="font-body text-[10px] text-white/50 leading-snug mt-0.5">{desc}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => toggleFlag(key)}
                          disabled={flagBusy}
                          data-testid={`dev-flag-${key}`}
                          aria-pressed={on}
                          className={`relative w-10 h-5 rounded-full border transition-all flex-shrink-0 ${
                            on
                              ? "border-[#F2C94C] bg-[#F2C94C]/25 shadow-[0_0_12px_-2px_rgba(242,201,76,0.6)]"
                              : "border-white/15 bg-[#0A0A0A]"
                          } disabled:opacity-40`}
                        >
                          <span
                            className={`absolute top-0.5 w-3.5 h-3.5 rounded-full transition-all ${
                              on ? "left-[calc(100%-1.125rem)] bg-[#F2C94C]" : "left-0.5 bg-white/30"
                            }`}
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>

          {/* Footer — kill switch */}
          <div className="flex items-center justify-between px-4 py-2 border-t border-[#F2C94C]/40 bg-[#F2C94C]/[0.04]">
            <span className="font-mono-hy text-[9px] tracking-widest text-white/50">DEV MODE ACTIVE</span>
            <button
              type="button"
              onClick={() => { setDevMode(false); setOpen(false); }}
              data-testid="dev-panel-exit"
              className="font-mono-hy text-[9px] tracking-[0.16em] px-2.5 py-1 rounded-full border border-[#EB5757]/50 text-[#EB5757] hover:bg-[#EB5757]/10 hover:border-[#EB5757] transition-all"
            >
              EXIT DEV MODE
            </button>
          </div>
        </aside>
      )}
    </>
  );
}

function Stat({ label, value, good, bad, accent }) {
  const cls = bad
    ? "border-[#EB5757]/60 text-[#EB5757]"
    : good
    ? "border-[#27AE60]/50 text-[#27AE60]"
    : accent === "cyan"
    ? "border-[#00E5FF]/40 text-[#00E5FF]"
    : "border-white/10 text-white";
  return (
    <div className={`rounded-lg border ${cls} bg-[#0A0A0A]/40 px-2 py-1.5`}>
      <div className="font-mono-hy text-[9px] text-white/50 tracking-widest">{label}</div>
      <div className="font-mono-hy text-[11px] mt-0.5">{value}</div>
    </div>
  );
}

function fmtUptime(s) {
  if (!s || s < 60) return `${s || 0}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

function fmtTimeAgo(iso) {
  if (!iso) return "—";
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(1, Math.floor((Date.now() - t) / 1000));
    if (diff < 60) return `${diff}s`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  } catch { return "—"; }
}
